import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';

import { IndustryCatalogType, PageResponse, Pagination } from '@core/shared/domain';
import { QueryParams } from '@core/shared/util';

import {
  CreatePlatformCatalog,
  FlyoutIndustryCatalog,
  IndustryCatalogAssignment,
  IndustryCatalogSortDto,
  MarkPublishResponse,
  PlatformCatalog,
  PlatformCatalogAssignedOrganization,
  PlatformCatalogAvailableOrganization,
  UpdatePlatformCatalog,
} from './plattformkatalog';
import {
  AssignedIndustryCatalogFilterQueryModel,
  MarkPublishFilter,
  PlatformCatalogGlobalFilter,
} from './plattformkatalog-filter';

@Injectable({ providedIn: 'root' })
export class PlattformkatalogeService {
  private readonly baseApiPath = '/api/contentmanagement/platformcatalogs';

  constructor(private httpClient: HttpClient) {}

  /* Plattformkataloge Operationen: */

  getPlattformkataloge(filter?: PlatformCatalogGlobalFilter): Observable<PlatformCatalog[]> {
    const params = QueryParams.build<PlatformCatalog>()
      .withoutPagination()
      .param('searchTerm', filter?.searchTerm === '' ? undefined : filter?.searchTerm)
      .param('field', filter?.field)
      .param('catalogType', filter?.catalogType)
      .param('dataSources', filter?.dataSources)
      .param('updatedOnly', filter?.updatedOnly)
      .toHttpParams();

    return this.httpClient
      .get<PageResponse<PlatformCatalog>>(`${this.baseApiPath}`, { params })
      .pipe(map((response) => response.data));
  }

  getAllDataSources(): Observable<string[]> {
    return this.httpClient.get<string[]>(`${this.baseApiPath}/industrycatalogs/datasources`);
  }

  createPlattformkatalog(plattformKatalog: CreatePlatformCatalog): Observable<PlatformCatalog> {
    return this.httpClient.post<PlatformCatalog>(`${this.baseApiPath}`, plattformKatalog);
  }

  publishPlattformkatalog(id: string): Observable<void> {
    return this.httpClient.put<void>(`${this.baseApiPath}/${id}/publish`, {});
  }

  /* (Genestete) Industriekataloge ({@link IndustriekatalogZuweisung}) Operationen: */

  getIndustriekatalogeForPlattformkatalog(
    platformCatalogId: string,
    filter?: AssignedIndustryCatalogFilterQueryModel,
  ): Observable<PageResponse<IndustryCatalogAssignment>> {
    const queryParams = QueryParams.build<IndustryCatalogAssignment>()
      .page(filter?.page ?? 1)
      .pageSize(filter?.pageSize ?? 50)
      .param('searchTerm', filter?.searchTerm)
      .param('field', filter?.field);

    if (filter?.catalogType && filter.catalogType.length > 0) {
      queryParams.param('catalogType', filter.catalogType);
    }

    if (filter?.dataSources && filter.dataSources?.length > 0) {
      queryParams.param('dataSources', filter.dataSources);
    }

    if (filter?.isUpdated) {
      queryParams.param('isUpdated', 'true');
    }

    return this.httpClient.get<PageResponse<IndustryCatalogAssignment>>(
      `${this.baseApiPath}/${platformCatalogId}/industrycatalogs`,
      {
        params: queryParams.toHttpParams(),
      },
    );
  }

  removeIndustriekatalogeFromPlattformkatalog(id: string, katalogId: string, remove = true): Observable<void> {
    return this.httpClient.put<void>(`${this.baseApiPath}/${id}/industrycatalogs/${katalogId}/removed`, {
      isRemoved: remove,
    });
  }

  addIndustriekatalogeToPlattformkatalog(id: string, katalogIds: Array<string>): Observable<void> {
    return this.httpClient.post<void>(`${this.baseApiPath}/${id}/industrycatalogs`, katalogIds);
  }

  sortIndustriekatalogeForPlattformkatalog(
    id: string,
    { id: katalogId, newIndex }: IndustryCatalogSortDto,
  ): Observable<void> {
    const body = {
      newIndex,
    };

    return this.httpClient.put<void>(`${this.baseApiPath}/${id}/industrycatalogs/${katalogId}/order`, body);
  }

  markIndustriekatalogeForPublishing(
    plattformkatalogId: string,
    publish: boolean,
    industryCatalogIds?: Array<string>,
    filter?: MarkPublishFilter,
  ): Observable<MarkPublishResponse> {
    const body = {
      publish,
      industryCatalogIds,
      ...filter,
    };

    return this.httpClient
      .put<{ data: MarkPublishResponse }>(`${this.baseApiPath}/${plattformkatalogId}/mark-publish`, body)
      .pipe(
        // FIXME: Is this really needed? Or is the typing just wrong for the MarkPublishResponse model?
        map(({ data }) => data),
      );
  }

  reindexPlattformkatalog(catalogId: string): Observable<void> {
    return this.httpClient.put<void>(`${this.baseApiPath}/${catalogId}/reindex`, {});
  }

  /**
   * Changes properties of an existing platform catalog and returns the updated catalog.
   */
  updatePlatformCatalog(catalogId: string, dto: UpdatePlatformCatalog): Observable<PlatformCatalog> {
    return this.httpClient.put<PlatformCatalog>(`${this.baseApiPath}/${catalogId}`, dto);
  }

  deletePlatformCatalog(catalogId: string): Observable<void> {
    return this.httpClient.delete<void>(`${this.baseApiPath}/${catalogId}`);
  }

  // -- Flyout Endpunkte (nutzen andere Interfaces) --

  // Flyout im Create Mode
  getAllIndustriekatalogeInCreateFlyout(
    filter: { searchTerm?: string; catalogType?: Array<IndustryCatalogType>; excludeIds?: Array<string> } = {},
    pagination?: Pagination, // TODO: Pagination wurde immer auf disabled gesetzt
  ): Observable<PageResponse<FlyoutIndustryCatalog>> {
    const body = {
      ...filter,
      pagination: false,
    };

    return this.httpClient.post<PageResponse<FlyoutIndustryCatalog>>(
      'api/contentmanagement/industrycatalogs-unassigned',
      body,
    );
  }

  // "Select All"-Button im Flyout
  getUnassignedIndustriekatalogePerSelectAllButton(
    id: string,
    filter: { searchTerm?: string; catalogType?: Array<IndustryCatalogType>; excludeIds?: Array<string> } = {},
  ): Observable<PageResponse<FlyoutIndustryCatalog>> {
    const body = {
      ...filter,
      platformCatalogId: id,
      pagination: false,
    };

    return this.httpClient.post<PageResponse<FlyoutIndustryCatalog>>(
      'api/contentmanagement/industrycatalogs-unassigned',
      body,
    );
  }

  // Flyout eines bestehenden Plattformkatalogs
  getUnassignedIndustriekatalogeForPlattformkatalog(
    id: string,
    filter: { searchTerm?: string; catalogType?: Array<IndustryCatalogType>; excludeIds?: Array<string> } = {},
    pagination?: Pagination,
  ): Observable<PageResponse<FlyoutIndustryCatalog>> {
    const body = {
      ...filter,
      platformCatalogId: id,
      ...pagination,
    };

    return this.httpClient.post<PageResponse<FlyoutIndustryCatalog>>(
      'api/contentmanagement/industrycatalogs-unassigned',
      body,
    );
  }

  // Request for the organizations of the platform catalog - the following requests are for the organization selector
  getPlatformCatalogOrganizations(
    platformCatalogId: string,
    searchTerm?: string,
  ): Observable<PlatformCatalogAssignedOrganization[]> {
    let params;
    if (searchTerm) {
      params = QueryParams.build().param('searchTerm', searchTerm).toHttpParams();
    }

    return this.httpClient.get<PlatformCatalogAssignedOrganization[]>(
      `api/contentmanagement/platformcatalogs/${platformCatalogId}/organizations`,
      { params },
    );
  }

  // Request for the available organizations to select and assign to a platform catalog
  getAvailableOrganizations(): Observable<PlatformCatalogAvailableOrganization[]> {
    return this.httpClient.get<PlatformCatalogAvailableOrganization[]>(
      'api/contentmanagement/platformcatalogs/available-organizations',
    );
  }

  // Request to save added organizations
  setOrganizations(
    platformCatalogId: string,
    organizationIds: Array<string>,
  ): Observable<PlatformCatalogAssignedOrganization[]> {
    return this.httpClient.put<PlatformCatalogAssignedOrganization[]>(
      `api/contentmanagement/platformcatalogs/${platformCatalogId}/organizations`,
      organizationIds,
    );
  }
}
